import type { GuestLinkDebugSnapshot, HardwareAccelerationKind, StreamDebugOverlaySnapshot, VideoStreamingStatus, NativeEncodeDebug } from '@playcastdotio/PeerCore';
import { formatGuestLinkDebugLine, formatStreamDebugOverlayLine, formatVideoPathDebugLine } from '@playcastdotio/PeerCore';
import type { PeerStats } from '../../../coordinators/peer/managers/PeerStatsManager';

export type StreamDebugOverlayProps = {
  snapshot: StreamDebugOverlaySnapshot;
};

/**
 * Maps encode hardware status + outbound PeerStats into the Phase 1 overlay snapshot.
 * Prefer the peer with the highest outbound FPS when multiple peers are connected.
 */
export function buildHostStreamDebugSnapshot(
  encodeKind: HardwareAccelerationKind,
  peerStats: PeerStats[],
  encoderImplementation?: string,
  negotiatedCodec?: string,
  offeredProfiles?: string[],
  sendCodecPin?: string,
  /** Looks up the guest end of the link for the peer the overlay settled on. */
  guestLinkFor?: (peerId: string) => GuestLinkDebugSnapshot | undefined,
  /** Which video path (Electron / native C++) that same peer is actually on. */
  videoPathFor?: (peerId: string) => VideoStreamingStatus | undefined,
  nativeSender?: {
    encode?: NativeEncodeDebug;
    /** Live capture size, used until the sender report carries its own picture. */
    capture?: { width: number; height: number } | null;
    latency?: { lastMs: number | null; floorMs: number | null } | null;
  },
): StreamDebugOverlaySnapshot {
  const best = peerStats.reduce<PeerStats | undefined>((acc, stats) => {
    if (!acc) return stats;
    return (stats.framesPerSecond ?? 0) >= (acc.framesPerSecond ?? 0) ? stats : acc;
  }, undefined);

  const videoPath = best ? videoPathFor?.(best.peerId) : undefined;
  const native = videoPath?.active === 'native';
  const nativeWebCodecs = native && videoPath.playback === 'webcodecs';
  const encode = native ? nativeSender?.encode : undefined;
  const guestLink = nativeGuestLink(best ? guestLinkFor?.(best.peerId) : undefined, videoPath, nativeWebCodecs ? nativeSender?.latency : undefined);
  const width = encode?.frameWidth || (native ? nativeSender?.capture?.width : undefined) || best?.frameWidth || 0;
  const height = encode?.frameHeight || (native ? nativeSender?.capture?.height : undefined) || best?.frameHeight || 0;
  return {
    codecKind: native ? (encode ? 'hardware' : 'unknown') : encodeKind,
    framesPerSecond: native ? encode?.framesPerSecond ?? null : (best?.framesPerSecond ?? 0),
    frameWidth: width,
    frameHeight: height,
    implementation: native ? (encode ? nativeEncodeDetail(encode) : undefined) : encoderImplementation,
    negotiatedCodec: native && videoPath?.codec ? videoPath.codec : negotiatedCodec,
    offeredProfiles: native ? undefined : offeredProfiles,
    sendCodecPin: native ? undefined : sendCodecPin,
    ...(guestLink ? { guestLink } : {}),
    ...(videoPath ? { videoPath } : {}),
  };
}

/** Electron inbound jitter is 0 while that sender is paused. Keep a real jitter buffer; drop the zero. */
function nativeGuestLink(
  guest: GuestLinkDebugSnapshot | undefined,
  videoPath: VideoStreamingStatus | undefined,
  latency: { lastMs: number | null; floorMs: number | null } | null | undefined,
): GuestLinkDebugSnapshot | undefined {
  if (!guest || videoPath?.playback !== 'webcodecs') return guest;
  const { jitterMs, ...rest } = guest;
  return {
    ...rest,
    ...(guest.jitterBufferMs !== undefined && jitterMs !== undefined ? { jitterMs } : {}),
    ...(guest.codec || !videoPath.codec ? {} : { codec: videoPath.codec }),
    ...(guest.glassToGlassMs !== undefined || latency?.lastMs == null ? {} : { glassToGlassMs: latency.lastMs }),
    ...(guest.glassToGlassFloorMs !== undefined || latency?.floorMs == null ? {} : { glassToGlassFloorMs: latency.floorMs }),
  };
}

function nativeEncodeDetail(encode: NativeEncodeDebug): string | undefined {
  return [encode.queueP50Us === undefined ? undefined : `q ${(encode.queueP50Us / 1000).toFixed(1)}ms`,
    encode.encodeP50Ms ? `enc ${encode.encodeP50Ms.toFixed(1)}ms` : undefined].filter(Boolean).join(' · ') || undefined;
}

/**
 * Presentational host Beacon stream debug lines: the host's encode on the first, the guest's
 * end of the link (codec, jitter buffer, glass-to-glass) on the second once a guest reports.
 * Non-interactive — does not capture pointer events.
 */
export function StreamDebugOverlay({ snapshot }: StreamDebugOverlayProps) {
  return (
    <div
      className='pointer-events-none mb-2 font-mono text-xs text-emerald-300'
      data-testid='stream-debug-overlay'
      aria-hidden='true'
    >
      <div>{formatStreamDebugOverlayLine(snapshot)}</div>
      {snapshot.guestLink ? (
        <div data-testid='stream-debug-overlay-guest'>{formatGuestLinkDebugLine(snapshot.guestLink)}</div>
      ) : null}
      {snapshot.videoPath ? (
        <div data-testid='stream-debug-overlay-video-path'>{formatVideoPathDebugLine(snapshot.videoPath)}</div>
      ) : null}
    </div>
  );
}
